import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAudioToSDKAudio } from '@wix/sdk-runtime/transformations/audio';
import { transformRESTDocumentToSDKDocument } from '@wix/sdk-runtime/transformations/document';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkImportFilesOptions,
  BulkImportFilesResponse,
  BulkUpdateItemOptions,
  BulkUpdateItemResponse,
  EnterpriseItemCreatedEnvelope,
  EnterpriseItemItemCategoriesChangedEnvelope,
  EnterpriseItemPublishStatusChangedEnvelope,
  EnterpriseItemUpdatedEnvelope,
  EnterpriseMediaItem,
  EnterpriseMediaItemQuery,
  EnterpriseMediaItemSearch,
  GenerateFileUploadUrlOptions,
  GenerateFileUploadUrlResponse,
  ImportFileOptions,
  ImportFileRequest,
  ImportFileResponse,
  ItemUploadCallbackOptions,
  ItemUploadCallbackResponse,
  ItemsQueryBuilder,
  LinkItemToCategoriesOptions,
  LinkItemToCategoriesResponse,
  OverwriteItemCategoriesOptions,
  OverwriteItemCategoriesResponse,
  QueryItemsResponse,
  SearchItemsResponse,
  UnlinkItemFromCategoriesOptions,
  UnlinkItemFromCategoriesResponse,
  UpdateItem,
  UpdateItemRequest,
  bulkImportFiles as universalBulkImportFiles,
  bulkUpdateItem as universalBulkUpdateItem,
  generateFileUploadUrl as universalGenerateFileUploadUrl,
  getItem as universalGetItem,
  importFile as universalImportFile,
  itemUploadCallback as universalItemUploadCallback,
  linkItemToCategories as universalLinkItemToCategories,
  overwriteItemCategories as universalOverwriteItemCategories,
  queryItems as universalQueryItems,
  searchItems as universalSearchItems,
  typedQueryItems as universalTypedQueryItems,
  unlinkItemFromCategories as universalUnlinkItemFromCategories,
  updateItem as universalUpdateItem,
} from './media-enterprise-public-media-v1-enterprise-item-enterprise-media-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/media' };

export function itemUploadCallback(
  httpClient: HttpClient
): ItemUploadCallbackSignature {
  return (options?: ItemUploadCallbackOptions) =>
    universalItemUploadCallback(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ItemUploadCallbackSignature {
  /**
   * Internal endpoint called by the public media backend when a file is created. Creates domain events for the Enterprise Public Media system.
   */
  (options?: ItemUploadCallbackOptions): Promise<ItemUploadCallbackResponse>;
}

export function generateFileUploadUrl(
  httpClient: HttpClient
): GenerateFileUploadUrlSignature {
  return (options?: GenerateFileUploadUrlOptions) =>
    universalGenerateFileUploadUrl(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateFileUploadUrlSignature {
  /**
   * Generates an upload URL for uploading local files to the Enterprise Public Media system. After uploading the file to the returned URL, the system processes it asynchronously. The item becomes available once processing completes.
   */
  (options?: GenerateFileUploadUrlOptions): Promise<
    NonNullablePaths<GenerateFileUploadUrlResponse, `uploadUrl`, 2>
  >;
}

export function importFile(httpClient: HttpClient): ImportFileSignature {
  return (url: string, options?: ImportFileOptions) =>
    universalImportFile(
      url,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ImportFileSignature {
  /**
   * Imports a file from a URL into the Enterprise Public Media system.
   *
   * To import a single file from your local machine, call [Generate File Upload Url](#generatefileuploadurl).
   * @param - URL of the file to import.
   */
  (url: string, options?: ImportFileOptions): Promise<
    NonNullablePaths<
      ImportFileResponse,
      | `item._id`
      | `item.mediaType`
      | `item.assets.archive._id`
      | `item.assets.archive.url`
      | `item.assets.model3d._id`
      | `item.assets.model3d.url`
      | `item.displayTags`
      | `item.internalTags`
      | `item.systemTags`
      | `item.parentCategoryIds`
      | `item.publishStatus`,
      5
    >
  >;
}

export function bulkImportFiles(
  httpClient: HttpClient
): BulkImportFilesSignature {
  return (
    importFileRequests: NonNullablePaths<ImportFileRequest, `url`, 2>[],
    options?: BulkImportFilesOptions
  ) =>
    universalBulkImportFiles(
      importFileRequests,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkImportFilesSignature {
  /**
   * Imports multiple files from URLs in a single API call. Check the `itemMetadata` in each result to identify successes and failures, and use `bulkActionMetadata` for an overview of total succeeded and failed operations.
   *
   * To import a single file from a URL, call [Import File](#importfile).
   * @param - List of files to import.
   */
  (
    importFileRequests: NonNullablePaths<ImportFileRequest, `url`, 2>[],
    options?: BulkImportFilesOptions
  ): Promise<
    NonNullablePaths<
      BulkImportFilesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item._id`
      | `results.${number}.item.mediaType`
      | `results.${number}.item.publishStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function searchItems(httpClient: HttpClient): SearchItemsSignature {
  return (query: EnterpriseMediaItemSearch) =>
    universalSearchItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchItemsSignature {
  /**
   * Searches for items using text-based search criteria.
   *
   * Search Items runs with these defaults, which you can override:
   *
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * All filters support equality only. Each search must include a `categoryId` filter. Use this endpoint when you need to search for items by text. For retrieving items with custom sorting, use [Query Items](#queryitems).
   *
   * To learn about working with search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
   * @param - Search query for text-based matching across item `title`, `displayTags`, and `internalTags` fields. Partial matching is supported for `title` and `displayTags`. `internalTags` require a full exact match. All filters support equality only. Each query must include a `categoryId` filter.
   */
  (query: EnterpriseMediaItemSearch): Promise<
    NonNullablePaths<
      SearchItemsResponse,
      | `items`
      | `items.${number}._id`
      | `items.${number}.mediaType`
      | `items.${number}.assets.archive._id`
      | `items.${number}.assets.archive.url`
      | `items.${number}.assets.model3d._id`
      | `items.${number}.assets.model3d.url`
      | `items.${number}.publishStatus`,
      6
    >
  >;
}

export function queryItems(httpClient: HttpClient): QueryItemsSignature {
  return () =>
    universalQueryItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryItemsSignature {
  /**
   * Retrieves a list of up to 200 items, given the provided paging, filtering, and sorting.
   *
   * Query Items runs with these defaults, which you can override:
   *
   * - Sorted by curation order
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * Each query must include a `categoryId` filter. For text-based search, use [Search Items](#searchitems).
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   */
  (): ItemsQueryBuilder;
}

export function typedQueryItems(
  httpClient: HttpClient
): TypedQueryItemsSignature {
  return (query: EnterpriseMediaItemQuery) =>
    universalTypedQueryItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryItemsSignature {
  /** */
  (query: EnterpriseMediaItemQuery): Promise<
    NonNullablePaths<
      QueryItemsResponse,
      | `items`
      | `items.${number}._id`
      | `items.${number}.mediaType`
      | `items.${number}.assets.archive._id`
      | `items.${number}.assets.archive.url`
      | `items.${number}.assets.model3d._id`
      | `items.${number}.assets.model3d.url`
      | `items.${number}.publishStatus`,
      6
    >
  >;
}

export function updateItem(httpClient: HttpClient): UpdateItemSignature {
  return (_id: string, item: UpdateItem) =>
    universalUpdateItem(
      _id,
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateItemSignature {
  /**
   * Updates an item.
   * To update multiple items in a single API call, call [Bulk Update Item](#bulkupdateitem).
   * @param - Item ID.
   * @returns Updated item.
   */
  (_id: string, item: UpdateItem): Promise<
    NonNullablePaths<
      EnterpriseMediaItem,
      | `_id`
      | `mediaType`
      | `assets.archive._id`
      | `assets.archive.url`
      | `assets.model3d._id`
      | `assets.model3d.url`
      | `displayTags`
      | `internalTags`
      | `systemTags`
      | `parentCategoryIds`
      | `publishStatus`,
      4
    >
  >;
}

export function bulkUpdateItem(
  httpClient: HttpClient
): BulkUpdateItemSignature {
  return (
    updateItemRequests: NonNullablePaths<UpdateItemRequest, `item._id`, 3>[],
    options?: BulkUpdateItemOptions
  ) =>
    universalBulkUpdateItem(
      updateItemRequests,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateItemSignature {
  /**
   * Updates multiple items in a single API call.
   *
   * To update a single item, call [Update Item](#updateitem).
   * @param - List of items to update. Each item must include an ID.
   */
  (
    updateItemRequests: NonNullablePaths<UpdateItemRequest, `item._id`, 3>[],
    options?: BulkUpdateItemOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateItemResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item._id`
      | `results.${number}.item.mediaType`
      | `results.${number}.item.publishStatus`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getItem(httpClient: HttpClient): GetItemSignature {
  return (itemId: string) =>
    universalGetItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetItemSignature {
  /**
   * Retrieves an item by ID.
   * @param - Item ID.
   * @returns Retrieved item.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      EnterpriseMediaItem,
      | `_id`
      | `mediaType`
      | `assets.archive._id`
      | `assets.archive.url`
      | `assets.model3d._id`
      | `assets.model3d.url`
      | `displayTags`
      | `internalTags`
      | `systemTags`
      | `parentCategoryIds`
      | `publishStatus`,
      4
    >
  >;
}

export function linkItemToCategories(
  httpClient: HttpClient
): LinkItemToCategoriesSignature {
  return (itemId: string, options?: LinkItemToCategoriesOptions) =>
    universalLinkItemToCategories(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface LinkItemToCategoriesSignature {
  /**
   * Links an item to multiple categories. Adds to existing category links without removing them.
   *
   * To replace all category links, call [Overwrite Item Categories](#overwriteitemcategories). To remove specific category links, call [Unlink Item From Categories](#unlinkitemfromcategories).
   * @param - Item ID.
   */
  (itemId: string, options?: LinkItemToCategoriesOptions): Promise<
    NonNullablePaths<LinkItemToCategoriesResponse, `linkedCategoryIds`, 2>
  >;
}

export function unlinkItemFromCategories(
  httpClient: HttpClient
): UnlinkItemFromCategoriesSignature {
  return (itemId: string, options?: UnlinkItemFromCategoriesOptions) =>
    universalUnlinkItemFromCategories(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UnlinkItemFromCategoriesSignature {
  /**
   * Unlinks an item from multiple categories. Removes specified category links while keeping others.
   *
   * To replace all category links, call [Overwrite Item Categories](#overwriteitemcategories). To add category links, call [Link Item To Categories](#linkitemtocategories).
   * @param - Item ID.
   */
  (itemId: string, options?: UnlinkItemFromCategoriesOptions): Promise<
    NonNullablePaths<UnlinkItemFromCategoriesResponse, `unlinkedCategoryIds`, 2>
  >;
}

export function overwriteItemCategories(
  httpClient: HttpClient
): OverwriteItemCategoriesSignature {
  return (itemId: string, options?: OverwriteItemCategoriesOptions) =>
    universalOverwriteItemCategories(
      itemId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface OverwriteItemCategoriesSignature {
  /**
   * Replaces all existing category links with the specified categories.
   *
   * To add category links without removing existing ones, call [Link Item To Categories](#linkitemtocategories). To remove specific category links, call [Unlink Item From Categories](#unlinkitemfromcategories).
   * @param - Item ID.
   */
  (itemId: string, options?: OverwriteItemCategoriesOptions): Promise<
    NonNullablePaths<
      OverwriteItemCategoriesResponse,
      `linkedCategoryIds` | `unlinkedCategoryIds`,
      2
    >
  >;
}

export const onEnterpriseItemCreated = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_item_created',
  true,
  (event: EnterpriseItemCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.assets.archive.urlExpirationDate' },
            { path: 'entity.assets.model3d.urlExpirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.assets.image' },
            { path: 'entity.assets.vector' },
            { path: 'entity.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'entity.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.assets.document' }],
        },
      ])
    )
)<EnterpriseItemCreatedEnvelope>();
export const onEnterpriseItemItemCategoriesChanged = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_item_item_categories_changed',
  true,
  (event: EnterpriseItemItemCategoriesChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.entity.createdDate' },
            { path: 'data.entity.updatedDate' },
            { path: 'data.entity.assets.archive.urlExpirationDate' },
            { path: 'data.entity.assets.model3d.urlExpirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'data.entity.assets.image' },
            { path: 'data.entity.assets.vector' },
            { path: 'data.entity.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'data.entity.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'data.entity.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'data.entity.assets.document' }],
        },
      ])
    )
)<EnterpriseItemItemCategoriesChangedEnvelope>();
export const onEnterpriseItemPublishStatusChanged = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_item_publish_status_changed',
  true,
  (event: EnterpriseItemPublishStatusChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.entity.createdDate' },
            { path: 'data.entity.updatedDate' },
            { path: 'data.entity.assets.archive.urlExpirationDate' },
            { path: 'data.entity.assets.model3d.urlExpirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'data.entity.assets.image' },
            { path: 'data.entity.assets.vector' },
            { path: 'data.entity.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'data.entity.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'data.entity.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'data.entity.assets.document' }],
        },
      ])
    )
)<EnterpriseItemPublishStatusChangedEnvelope>();
export const onEnterpriseItemUpdated = EventDefinition(
  'wix.media.enterprise_public_media.v1.enterprise_item_updated',
  true,
  (event: EnterpriseItemUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.assets.archive.urlExpirationDate' },
            { path: 'entity.assets.model3d.urlExpirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.assets.image' },
            { path: 'entity.assets.vector' },
            { path: 'entity.assets.model3d.thumbnail' },
          ],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.assets.video' }],
        },
        {
          transformFn: transformRESTAudioToSDKAudio,
          paths: [{ path: 'entity.assets.audio' }],
        },
        {
          transformFn: transformRESTDocumentToSDKDocument,
          paths: [{ path: 'entity.assets.document' }],
        },
      ])
    )
)<EnterpriseItemUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  ApplicationError,
  Archive,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkImportFilesOptions,
  BulkImportFilesRequest,
  BulkImportFilesResponse,
  BulkImportFilesResult,
  BulkItemUpdateResult,
  BulkUpdateItemOptions,
  BulkUpdateItemRequest,
  BulkUpdateItemResponse,
  Cursors,
  DomainEvent,
  DomainEventBodyOneOf,
  EnterpriseItemCreatedEnvelope,
  EnterpriseItemItemCategoriesChangedEnvelope,
  EnterpriseItemPublishStatusChangedEnvelope,
  EnterpriseItemUpdatedEnvelope,
  EnterpriseMediaItem,
  EnterpriseMediaItemQuerySpec,
  EnterpriseMediaItemSearchSpec,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GenerateFileUploadUrlOptions,
  GenerateFileUploadUrlRequest,
  GenerateFileUploadUrlResponse,
  GetItemRequest,
  GetItemResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImportFileOptions,
  ImportFileRequest,
  ImportFileResponse,
  ItemAssets,
  ItemAssetsAssetsOneOf,
  ItemCategoriesChanged,
  ItemMetadata,
  ItemUploadCallbackOptions,
  ItemUploadCallbackRequest,
  ItemUploadCallbackResponse,
  ItemsQueryBuilder,
  ItemsQueryResult,
  LinkItemToCategoriesOptions,
  LinkItemToCategoriesRequest,
  LinkItemToCategoriesResponse,
  MediaType,
  MessageEnvelope,
  Model3D,
  OverwriteItemCategoriesOptions,
  OverwriteItemCategoriesRequest,
  OverwriteItemCategoriesResponse,
  Paging,
  PagingMetadataV2,
  PublishStatus,
  PublishStatusChanged,
  QueryItemsRequest,
  QueryItemsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  Search,
  SearchDetails,
  SearchItemsRequest,
  SearchItemsResponse,
  SearchPagingMethodOneOf,
  SortOrder,
  Sorting,
  UnlinkItemFromCategoriesOptions,
  UnlinkItemFromCategoriesRequest,
  UnlinkItemFromCategoriesResponse,
  UpdateItem,
  UpdateItemRequest,
  UpdateItemResponse,
  VideoResolution,
  WebhookIdentityType,
  utils,
} from './media-enterprise-public-media-v1-enterprise-item-enterprise-media-items.universal.js';
